<template>
    <div
        class="item"
        :class="getItemStyle"
        @click="recoverHandler(item.id)"
    >
        <div class="wrap">
            <div class="indicator-wrap">
                <div
                    v-if="showIndicator"
                    class="indicator"
                    :class="indicatorStyle"
                ></div>
                <div
                    v-if="showCheckbox"
                    class="checkbox"
                    :class="{checked: isChecked}"
                >
                    <or-icon icon="done" />
                    <input
                        type="checkbox"
                        :checked="isChecked"
                        @click.prevent.stop="toggleCheckbox"
                    >
                </div>
            </div>
            <div class="label-wrap">
                <input
                    v-if="renameMode"
                    v-focus
                    v-model="newLabel"
                    @keyup.esc="cancelHandler"
                    @keyup.enter="confirmRename"
                >
                <span
                    v-else
                    class="label"
                    :class="{bold: this.isCurrentVersion}"
                >
                    {{item.name}}
                </span>
            </div>
            <div
                v-if="!renameMode"
                class="time-wrap"
            >
                <span class="time">
                    {{timeLabel}}
                </span>
            </div>
            <div
                v-if="!editMode && !renameMode"
                class="tools-wrap"
            >
                <div
                    class="icon-wrap"
                    @click.prevent.stop="editVersionName"
                >
                    <or-icon
                        :class="{ disabled: renameIconDisabled }"
                        icon="edit"
                    />
                </div>
                <div
                    class="icon-wrap"
                    @click.prevent.stop="publishHandler(item.id)"
                >
                    <or-icon
                        v-if="showPublishBtn"
                        :class="{disabled: publishIconDisabled}"
                        icon="cloud_upload"
                    />
                </div>
            </div>
            <div
                v-if="!editMode && renameMode"
                class="rename-tools-wrap"
            >
                <div
                    v-if="!showRenameIndicator"
                    class="icon-wrap"
                    @click.prevent.stop="cancelHandler"
                >
                    <or-icon icon="cancel" />
                </div>
                <div
                    v-if="!showRenameIndicator"
                    class="icon-wrap"
                    @click.prevent.stop="confirmRename"
                >
                    <or-icon icon="check" />
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import { mapState, mapGetters, mapActions } from 'vuex'
import KnowledgeBus from '@/KnowledgeBus'
import versionsValidationMixin from '@/mixins/versions-validation.mixin'

export default {
  mixins: [versionsValidationMixin],

  directives: {
    focus: {
      inserted: function (el) {
        el.focus()
      }
    }
  },

  props: {
    item: Object,
    editMode: Boolean,
    checkedSet: Array,
    deleteVerProc: Object
  },

  computed: {
    ...mapState('knowledgebase/versions', [
      'publishVersionProcessing',
      'recoverVersionProcessing',
      'renameVersionProcessing'
    ]),
    ...mapGetters('knowledgebase', ['getCurrent']),
    ...mapGetters('knowledgebase/versions', ['getProductionVersionId']),

    isCurrentVersion () {
      return this.item.id === this.getProductionVersionId
    },

    labelIsInvalid () {
      return this.$v.newLabel.$invalid && this.$v.newLabel.$dirty
    },

    showCheckbox () {
      return this.editMode &&
            !this.item.isPublish &&
            !this.showDeletingIndicator &&
            !this.isCurrentVersion
    },

    showPublishBtn () {
      return !this.item.isPublish && !this.getCurrent.isOld
    },

    timeLabel () {
      let label = _.get(this.item, 'time')

      if (this.editMode && this.item.isPublish) {
        label = this.$t('model.nlu-model.sidebar.versions.version_item.errors.publishedVersionDeletion')
      }
      if (this.editMode && this.isCurrentVersion) {
        label = this.$t('model.nlu-model.sidebar.versions.version_item.errors.currentVersionDeletion')
      }

      return label
    },

    isChecked () {
      return _.includes(this.checkedSet, this.item.id)
    },

    getItemStyle () {
      return _.trim(`
            ${this.editMode ? 'edit' : ''}
            ${this.renameMode ? 'rename' : ''}
            ${this.labelIsInvalid ? 'invalid' : ''}
            ${this.recoverVersionProcessing.process ? 'disabled' : ''}
            ${this.publishVersionProcessing.process ? 'disabled' : ''}
        `)
    },

    showIndicator () {
      return this.showUnnownIndicator ||
                this.showDeletingIndicator ||
                this.showRenameIndicator ||
                this.showRecoverIndicator ||
                this.showPublishingIndicator
    },

    indicatorStyle () {
      return _.trim(`
                ${this.showRecoverIndicator ? 'indicator--recover' : ''}
                ${this.showPublishingIndicator ? 'indicator--publishing' : ''}
                ${this.showDeletingIndicator ? 'indicator--deleting' : ''}
            `)
    },

    showUnnownIndicator () {
      return this.item.isPublish && !this.recoverVersionProcessing.process &&
        !this.showPublishingIndicator
    },

    showDeletingIndicator () {
      return this.deleteVerProc.process &&
        _.includes(this.deleteVerProc.set, this.item.id)
    },

    showRenameIndicator () {
      return this.item.id === this.renameVersionProcessing.id
    },

    showRecoverIndicator () {
      return (this.recoverVersionProcessing.process &&
                this.recoverVersionProcessing.id === this.item.id &&
                !this.publishVersionProcessing.process) || this.showRenameIndicator
    },

    showPublishingIndicator () {
      return this.publishVersionProcessing.process &&
                this.item.id === this.publishVersionProcessing.id
    },

    publishIconDisabled () {
      return this.publishVersionProcessing.process ||
                this.recoverVersionProcessing.process
    },

    renameIconDisabled () {
      return this.publishVersionProcessing.process ||
                this.recoverVersionProcessing.process
    }
  },

  data () {
    return {
      newLabel: '',
      renameMode: false
    }
  },

  methods: {
    ...mapActions('knowledgebase', ['fetchCategories']),
    ...mapActions('knowledgebase/entities', ['fetchPostprocessEntities']),
    ...mapActions('knowledgebase/notifications', ['addError']),
    ...mapActions('knowledgebase/autogenerated', ['fetchAutogenerated']),
    ...mapActions('knowledgebase/versions', [
      'recoverVersion',
      'renameVersion',
      'publishVersion'
    ]),

    async recoverHandler (versionId) {
      if (this.editMode) {
        this.toggleCheckbox()
        return
      }
      if (this.renameMode ||
        this.recoverVersionProcessing.process ||
        this.publishVersionProcessing.process
      ) {
        return
      }

      await this.recoverVersion({ versionId })

      await Promise.all([
        this.fetchAutogenerated({ type: 'suggested', firstLoad: true }),
        this.fetchAutogenerated({ type: 'unrecognized', firstLoad: true }),
        this.fetchPostprocessEntities()
      ])
    },

    publishHandler (id) {
      this.renameMode = false
      if (this.publishVersionProcessing.process || this.recoverVersionProcessing.process) {
        return
      }
      this.publishVersion({ versionId: id })
    },

    editVersionName () {
      if (this.publishVersionProcessing.process || this.recoverVersionProcessing.process) {
        return
      }
      this.newLabel = _.get(_.cloneDeep(this.item), 'name', '')
      this.renameMode = true
    },

    toggleCheckbox () {
      if (!this.isCurrentVersion && !this.item.isPublish) {
        KnowledgeBus.$emit('checkbox::toggle', this.item.id)
      }
    },

    async confirmRename () {
      this.$v.newLabel.$touch()

      if (this.labelIsInvalid) {
        this.addError({
          titleLabel: 'model.nlu-model.sidebar.versions.version_item.renameVersionFailed',
          error: this.errors
        })
      } else {
        await this.renameVersion({ id: this.item.id, name: this.newLabel })
        this.renameMode = false
        this.$v.newLabel.$reset()
      }
    },

    cancelHandler () {
      this.renameMode = false
      this.$v.newLabel.$reset()
    }
  }
}
</script>

<style lang="scss" scoped>
    .item {
        display: flex;
        justify-content: space-between;
        align-items: center;
        padding: 0 16px 0;
        height: 32px;
        overflow: hidden;
        transition: all .2s ease;

        &.disabled {
            &:hover {
                cursor: auto;
                background-color: white;
            }

            .wrap > .label-wrap > .label {
                color: #dfdfdf;
            }

            .wrap > .time-wrap > .time {
                color: #dfdfdf;
            }
        }

        &.edit:hover {
            cursor: auto;
            background-color: white;
        }

        &.rename {
            background-color: #F6F6F6;

            &.invalid {
                input {
                    color: $or-red;
                    transition: all .2s ease;
                }
            }

            .wrap > .label-wrap {
                flex-grow: 1;
                max-width: 100%;
            }
        }

        &:hover {
            cursor: pointer;
            background-color: #F6F6F6;
        }

        .wrap {
            display: flex;
            width: 100%;

            .indicator-wrap {
                display: flex;
                justify-content: flex-end;
                align-items: center;
                width: 16px;
                margin-right: 8px;

                @keyframes blinking {
                    0% {
                        opacity: .2;
                    }

                    50% {
                        opacity: 1;
                    }

                    100% {
                        opacity: .2;
                    }
                }

                .indicator {
                    width: 8px;
                    height: 8px;
                    margin-top: 2px;
                    border-radius: 100%;
                    background-color: $or-blue;

                    &--recover {
                        background-color: $or-grey;
                        animation: blinking 1s infinite;
                    }

                    &--publishing {
                        background-color: $or-blue;
                        animation: blinking 1s infinite;
                    }

                    &--deleting {
                        background-color: $or-red;
                        animation: blinking 1s infinite;
                    }
                }

                .checkbox {
                    display: flex;
                    position: relative;
                    justify-content: center;
                    align-items: center;
                    width: 16px;
                    height: 16px;
                    border: 1px solid $or-grey-medium;
                    border-radius: 2px;

                    &.checked {
                        background-color: $or-blue;
                        border: none;

                        &:hover {
                            background-color: darken($or-blue, 5%);
                            transition: all .2s ease;
                        }

                        .material-icons {
                            display: block;
                        }
                    }

                    input {
                        position: absolute;
                        top: 0;
                        bottom: 0;
                        right: 0;
                        left: 0;
                        opacity: 0;

                        &:hover {
                            cursor: pointer;
                        }
                    }

                    .material-icons {
                        display: none;
                        color: #fff;
                        font-size: 12px;
                    }
                }
            }

            .label-wrap {
                display: flex;
                align-items: center;
                min-width: 100px;
                max-width: 100px;
                overflow: hidden;
                padding-right: 8px;

                input {
                    width: 100%;
                    background: transparent;
                    font-size: 14px;
                    border: none;
                    outline: none;

                    &:active, &:focus {
                        border: none;
                        outline: none;
                    }
                }

                .label {
                    overflow: hidden;
                    text-overflow: ellipsis;
                    white-space: nowrap;
                    font-size: 14px;
                    color: $or-black-blue;

                    &.bold {
                        font-weight: bold;
                    }
                }
            }

            .time-wrap {
                display: flex;
                flex-grow: 1;
                align-items: center;
                overflow: hidden;

                .time {
                    overflow: hidden;
                    text-overflow: ellipsis;
                    white-space: nowrap;
                    font-size: 12px;
                    color: $or-grey-dark;
                }
            }

            .rename-tools-wrap {
                margin-right: 32px;
            }

            .tools-wrap, .rename-tools-wrap {
                display: flex;

                .icon-wrap {
                    display: flex;
                    justify-content: space-around;
                    align-items: center;
                    min-width: 32px;
                    min-height: 32px;

                    &:hover {
                        cursor: pointer;

                        .material-icons:not(.disabled) {
                            color: darken($or-grey, 10%);
                        }
                    }

                    .material-icons {
                        font-size: 16px;
                        color: $or-grey;

                        &.disabled {
                            color: lighten($or-grey, 10%);

                            &:hover {
                                cursor: default;
                            }
                        }
                    }
                }
            }
        }
    }
</style>
